import { useEffect, useRef, useState } from "react";
import type { Annotation, AnnotationStroke } from "../../../shared/types";
import { listAnnotations, saveAnnotation, type AnnotationSave } from "../songs/music-api";
import { getNotes, noteKey, putNotes } from "./cache";

export function useAnnotations(songId: string | null, arrangementId: string | null, planId: string | null, page: number, enabled: boolean) {
  const [strokes, setStrokes] = useState<AnnotationStroke[]>([]);
  const [status, setStatus] = useState("");
  const pending = useRef<AnnotationSave | null>(null);
  const timer = useRef(0);

  async function flush() {
    const snapshot = pending.current;
    if (!snapshot) return;
    pending.current = null;
    const key = noteKey(snapshot.songId, snapshot.arrangementId, snapshot.planId, snapshot.pageIndex);
    const stamp = new Date().toISOString();
    await putNotes(key, { strokes: snapshot.strokes, updatedAt: stamp, pending: true }).catch(() => undefined);
    try {
      const saved = await saveAnnotation(snapshot);
      await putNotes(key, { strokes: snapshot.strokes, updatedAt: saved.updatedAt, pending: false });
      setStatus("Annotations saved");
    } catch {
      setStatus("Annotations kept on this device until the server is reachable.");
    }
  }

  function commit(next: AnnotationStroke[]) {
    if (!songId) return;
    pending.current = { songId, arrangementId, planId, pageIndex: page, strokes: next };
    setStrokes(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void flush(); }, 320);
  }

  useEffect(() => {
    void flush();
    setStrokes([]);
    if (!enabled || !songId) return;
    let cancelled = false;
    const key = noteKey(songId, arrangementId, planId, page);
    void (async () => {
      const local = await getNotes(key).catch(() => null);
      let remote: Annotation | null = null;
      try {
        const rows = await listAnnotations(songId, planId, arrangementId);
        remote = rows.find((row) => row.pageIndex === page) ?? null;
      } catch {
        remote = null;
      }
      if (cancelled) return;
      const localWins = Boolean(local && (!remote || local.updatedAt > remote.updatedAt));
      setStrokes(localWins && local ? local.strokes : remote?.strokes ?? local?.strokes ?? []);
      if (local?.pending && localWins) {
        pending.current = { songId, arrangementId, planId, pageIndex: page, strokes: local.strokes };
        void flush();
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timer.current);
    };
  }, [songId, arrangementId, planId, page, enabled]);

  useEffect(() => () => { void flush(); }, []);

  return {
    strokes,
    status,
    commit,
    undo: () => commit(strokes.slice(0, -1)),
    clear: () => commit([]),
  };
}
